import React from 'react'
import makeStyles from '@mui/styles/makeStyles'
import { Avatar, Box, Button } from '@mui/material'
import { Divider } from '@mui/material'
import { Badge } from '@mui/material'
import { CircularProgress } from '@mui/material'
import { ShowConditionally } from '@shared/components/show-conditionally.component'
import { RequirementFormConfig } from '@shared/types'
import { usePhotos } from './use-photos.hook'
import { RequirementPhotoDialog } from './requirement-photo-dialog.component'
import { Camera } from 'react-html5-camera-photo'
import { RequirementAddPhotoDialog } from './requirement-add-photo.dialog.component'
import { AddFileDialog } from '@shared/components/add-file.dialog.component'
import { originalIcons } from '@shared/components/icons/icons'

type Props = {
  editable?: boolean
  formConfig: RequirementFormConfig
}

export const PhotosGrid = ({ editable, formConfig }: Props) => {
  const classes = useStyles()
  const { requirementID, auditID } = formConfig
  const {
    photos,
    removeImageMutation,
    onImagePreviewOpenFactory,
    onPhotoRemoveFactory,
    onTakePhotoDialogOpen,
    onUploadPhotoDialogOpen,
    onPhotoDialogClose,
    handleOnTakePhotoAnimationDone,
    onPhotoDialogReset,
    onImagePreviewClose,
    currentImagePreview,
    isTakePhotoDialogOpen,
    isUploadPhotoDialogOpen,
    isImagePreviewOpen,
    isRemoveCamera,
    isUploadingPhoto,
    currentPhoto,
    onUploadPhotoDialogClose,
    savePhotos,
  } = usePhotos(formConfig, requirementID?.toString() || '', auditID!)

  return (
    <>
      <ShowConditionally when={editable}>
        <Box>
          <Button
            variant="text"
            color="primary"
            startIcon={<originalIcons.PhotoCamera />}
            onClick={onTakePhotoDialogOpen}
          >
            Zrób zdjęcie
          </Button>
          <Button
            variant="text"
            color="primary"
            startIcon={<originalIcons.PhotoCamera />}
            onClick={onUploadPhotoDialogOpen}
          >
            Dodaj zdjęcie z dysku
          </Button>
        </Box>
      </ShowConditionally>
      <ShowConditionally when={photos?.length > 0}>
        <>
          <ShowConditionally when={!removeImageMutation.isLoading}>
            <Box className={classes.grid}>
              {photos?.map((photo) => {
                return editable ? (
                  <Badge
                    key={photo.imageID}
                    color="secondary"
                    onClick={onPhotoRemoveFactory(photo)}
                    className={classes.imageContainer}
                    badgeContent="x"
                  >
                    <Avatar
                      variant="square"
                      src={photo.imageURL}
                      onClick={onImagePreviewOpenFactory(photo)}
                    />
                  </Badge>
                ) : (
                  <Avatar
                    key={photo.imageID}
                    variant="square"
                    src={photo.imageURL}
                    onClick={onImagePreviewOpenFactory(photo)}
                    className={classes.imageContainer}
                  />
                )
              })}
            </Box>
          </ShowConditionally>
          <ShowConditionally when={removeImageMutation.isLoading}>
            <CircularProgress className={classes.imagesProgress} />
          </ShowConditionally>
          <Divider variant="middle" className={classes.divider} />
        </>
      </ShowConditionally>
      {isImagePreviewOpen && (
        <RequirementPhotoDialog
          isImagePreviewOpen={isImagePreviewOpen}
          onImagePreviewClose={onImagePreviewClose}
          currentImagePreview={currentImagePreview}
        />
      )}
      {isTakePhotoDialogOpen && (
        <RequirementAddPhotoDialog
          isSubmitDisabled={isUploadingPhoto}
          open={isTakePhotoDialogOpen}
          fullWidth
          maxWidth={false}
          style={{ width: '100%', maxWidth: 'none' }}
          onReset={onPhotoDialogReset}
          onClose={onPhotoDialogClose}
        >
          {currentPhoto ? (
            <img
              alt=""
              style={{ display: 'block', margin: '0 auto' }}
              src={currentPhoto}
            />
          ) : !isRemoveCamera ? (
            <Camera
              idealFacingMode="environment"
              imageCompression={0.5}
              onTakePhotoAnimationDone={(dataUri) =>
                handleOnTakePhotoAnimationDone(dataUri)
              }
            />
          ) : null}
        </RequirementAddPhotoDialog>
      )}
      {isUploadPhotoDialogOpen && (
        <AddFileDialog
          open={isUploadPhotoDialogOpen}
          acceptedFiles={['image/jpeg', 'image/png']}
          getPreviewIcon={() => <originalIcons.ImageIcon />}
          getEndpoint={() =>
            `auditrequirements/${auditID}/${requirementID}/file`
          }
          onDialogClose={onUploadPhotoDialogClose}
          onCleanup={onUploadPhotoDialogClose}
          onAddFiles={savePhotos}
        />
      )}
    </>
  )
}

const useStyles = makeStyles((theme) => ({
  imageContainer: {
    cursor: 'pointer',
    marginRight: '20px',
  },
  imagePreviewTitle: {
    padding: 0,
    display: 'flex',
    flexFlow: 'row-reverse',
  },
  imagesProgress: {
    width: 40,
    height: 40,
    position: 'relative',
    top: 5,
    margin: '0 auto',
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    width: '100%',
    marginLeft: 0,
  },
  grid: {
    marginTop: theme.spacing(2),
  },
}))
